import { useState, useEffect, useRef } from 'react';
import { Modal } from '../ui/Modal';
import { Button, SectionLabel } from '@perch/ui';
import { CheckEmailScreen } from './CheckEmailScreen';
import {
  getEmailTypoSuggestion,
  getLastAuthMethod,
  saveAuthMethod,
} from './emailUtils';
import { posthog } from '../../lib/posthog';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSignIn: () => Promise<{ error: Error | null }>;
  onSignInWithMagicLink: (email: string) => Promise<{ error: Error | null }>;
  onDevSignIn?: (email: string, password: string) => Promise<{ error: Error | null }>;
}

const DEV_ACCOUNTS = [
  { label: 'New User', description: '0 bookings', email: 'newuser@perchtravel.com', password: 'TestPassword123!' },
  { label: 'Power User', description: '40 bookings', email: 'testuser@perchtravel.com', password: 'TestPassword123!' },
] as const;

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

/**
 * Auth modal supporting Google OAuth and magic link (passwordless email).
 *
 * Two views:
 *   'sign-in' — Google button + email form (with provider memory + typo detection)
 *   'check-email' — interstitial after magic link send (provider-aware smart button)
 *
 * Provider memory (localStorage) remembers the last auth method and swaps
 * button hierarchy: returning magic link users see the email form promoted
 * to primary, Google demoted to secondary.
 *
 * In dev builds, quick-login buttons appear for test accounts.
 */
export function AuthModal({ isOpen, onClose, onSignIn, onSignInWithMagicLink, onDevSignIn }: AuthModalProps) {
  const [view, setView] = useState<'sign-in' | 'check-email'>('sign-in');
  const [email, setEmail] = useState('');
  const [typoSuggestion, setTypoSuggestion] = useState<string | null>(null);
  const [authError, setAuthError] = useState('');
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const [devLoading, setDevLoading] = useState<string | null>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);

  // Provider memory — determine button hierarchy
  const lastAuth = getLastAuthMethod();
  const prefersMagicLink = lastAuth?.method === 'magic_link';

  // Pre-fill email from provider memory
  useEffect(() => {
    if (isOpen && lastAuth?.email) {
      setEmail(lastAuth.email);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setView('sign-in');
      setAuthError('');
      setMagicLinkLoading(false);
      setTypoSuggestion(null);
    }
  }, [isOpen]);

  const handleClose = () => {
    setAuthError('');
    onClose();
  };

  const handleGoogleSignIn = async () => {
    setAuthError('');
    saveAuthMethod('google');
    const { error } = await onSignIn();
    if (error) setAuthError(error.message);
  };

  const handleEmailChange = (value: string) => {
    const trimmed = value.trim();
    setEmail(trimmed);
    setTypoSuggestion(getEmailTypoSuggestion(trimmed));
  };

  const handleTypoAccept = () => {
    if (typoSuggestion) {
      posthog.capture('email_typo_corrected', { original: email, corrected: typoSuggestion });
      setEmail(typoSuggestion);
      setTypoSuggestion(null);
    }
  };

  const handleMagicLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || magicLinkLoading) return;

    setAuthError('');
    setMagicLinkLoading(true);

    const { error } = await onSignInWithMagicLink(email.toLowerCase());
    setMagicLinkLoading(false);

    if (error) {
      // Handle rate limiting specifically
      if (error.message?.includes('rate') || error.message?.includes('429')) {
        setAuthError('Please wait 60 seconds before trying again.');
      } else {
        setAuthError(error.message);
      }
      return;
    }

    saveAuthMethod('magic_link', email.toLowerCase());
    posthog.capture('magic_link_sent');
    setView('check-email');
  };

  const handleResend = async () => {
    const { error } = await onSignInWithMagicLink(email.toLowerCase());
    if (error) {
      setView('sign-in');
      setAuthError(error.message);
    }
  };

  const handleGoogleFallback = () => {
    setView('sign-in');
    handleGoogleSignIn();
  };

  const handleDevSignIn = async (devEmail: string, password: string) => {
    if (!onDevSignIn) return;
    setAuthError('');
    setDevLoading(devEmail);
    localStorage.removeItem('perch_onboarding_completed');
    const { error } = await onDevSignIn(devEmail, password);
    setDevLoading(null);
    if (error) setAuthError(error.message);
  };

  // Title changes based on view and returning user state
  const title = view === 'check-email'
    ? ''
    : prefersMagicLink
      ? 'Welcome Back.'
      : 'Sign In to Perch';

  // --- Shared UI pieces ---

  const googleButton = (variant: 'primary' | 'secondary') => (
    <Button
      variant={variant === 'primary' ? 'coral' : 'outline'}
      fullWidth
      onClick={handleGoogleSignIn}
      className="flex items-center justify-center gap-3"
    >
      <GoogleIcon />
      Continue with Google
    </Button>
  );

  const divider = (text: string) => (
    <div className="flex items-center gap-3">
      <div className="h-px flex-1 bg-stone-200/60 dark:bg-stone-800/50" />
      <SectionLabel>{text}</SectionLabel>
      <div className="h-px flex-1 bg-stone-200/60 dark:bg-stone-800/50" />
    </div>
  );

  const emailForm = (isPrimary: boolean) => (
    <form onSubmit={handleMagicLinkSubmit} className="space-y-3">
      <div>
        <input
          ref={emailInputRef}
          type="email"
          value={email}
          onChange={(e) => handleEmailChange(e.target.value)}
          placeholder="you@example.com"
          aria-label="Email address"
          autoComplete="email"
          className="w-full rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 px-4 py-3 text-sm text-stone-900 dark:text-stone-100 placeholder:text-stone-400 dark:placeholder:text-stone-500 focus:outline-none focus:ring-2 focus:ring-stone-900/20 dark:focus:ring-stone-100/20 transition-colors"
        />
        {typoSuggestion && (
          <button
            type="button"
            onClick={handleTypoAccept}
            className="mt-1.5 text-xs text-emerald-600 dark:text-emerald-400 hover:underline"
            role="alert"
            aria-live="polite"
          >
            Did you mean {typoSuggestion}?
          </button>
        )}
      </div>
      <Button
        variant={isPrimary ? 'coral' : 'outline'}
        fullWidth
        type="submit"
        disabled={!email || magicLinkLoading}
      >
        {magicLinkLoading ? 'Sending...' : 'Send Magic Link'}
      </Button>
    </form>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      size="sm"
    >
      {view === 'check-email' ? (
        <CheckEmailScreen
          email={email}
          onBack={() => setView('sign-in')}
          onResend={handleResend}
          onGoogleFallback={handleGoogleFallback}
        />
      ) : (
        <div className="space-y-5">
          {!prefersMagicLink && (
            <p className="text-sm text-stone-600 dark:text-stone-400 text-center">
              Start tracking in under a minute.
            </p>
          )}

          {authError && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-lg text-sm">
              {authError}
            </div>
          )}

          {/* Layout depends on provider memory */}
          {prefersMagicLink ? (
            <>
              {emailForm(true)}
              {divider('or')}
              {googleButton('secondary')}
            </>
          ) : (
            <>
              {googleButton('primary')}
              {divider('or sign in with email')}
              {emailForm(false)}
            </>
          )}

          <p className="text-xs text-stone-400 dark:text-stone-500 text-center">
            By continuing, you agree to our{' '}
            <a href="/terms" className="underline hover:text-stone-600 dark:hover:text-stone-300">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy" className="underline hover:text-stone-600 dark:hover:text-stone-300">
              Privacy Policy
            </a>.
          </p>

          {/* Dev quick-login — only in dev builds */}
          {!import.meta.env.PROD && onDevSignIn && (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-amber-300/40 dark:bg-amber-700/40" />
                <SectionLabel variant="amber">Dev Accounts</SectionLabel>
                <div className="h-px flex-1 bg-amber-300/40 dark:bg-amber-700/40" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                {DEV_ACCOUNTS.map((account) => (
                  <button
                    key={account.email}
                    onClick={() => handleDevSignIn(account.email, account.password)}
                    disabled={devLoading !== null}
                    className="px-3 py-2 rounded-full border border-amber-300/50 dark:border-amber-700/50 bg-amber-50 dark:bg-amber-950/30 text-xs text-amber-800 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors disabled:opacity-50"
                  >
                    <span className="font-medium">{account.label}</span>
                    <span className="block text-amber-600/70 dark:text-amber-400/60 mt-0.5">
                      {devLoading === account.email ? 'Signing in...' : account.description}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
